import { useMemo } from 'react';
import { DAY_NAMES } from '../types';
import type { EventInstance, TimeFormat } from '../types';
import type { WeatherDay } from '../types';
import { getWeekDays, getAllDayEvents, isToday, formatDateKey, addDays } from '../utils';
import { AllDayRow } from './AllDayRow';
import { TimeGrid } from './TimeGrid';
import { WeatherIcon } from './WeatherIcon';

interface WeekViewProps {
  currentDate: Date;
  events: EventInstance[];
  weekStartDay: number;
  workingHoursStart: number;
  timeFormat?: TimeFormat;
  onClickSlot: (date: Date, hour: number) => void;
  onClickEvent: (event: EventInstance) => void;
  resizingEventId?: string | null;
  resizeHeight?: number | null;
  onResizeStart?: (eventId: string, originalHeight: number, e: React.MouseEvent) => void;
  weather?: Map<string, WeatherDay>;
  showWeekends?: boolean;
  hourHeightPx?: number;
  weekViewAnchor?: 'fixed' | 'rolling';
  dimPastEvents?: boolean;
  eventColorPriority?: 'event' | 'calendar';
  showConflictWarnings?: boolean;
  minBreakBetweenEvents?: number;
}

export function WeekView({
  currentDate,
  events,
  weekStartDay,
  workingHoursStart,
  timeFormat,
  onClickSlot,
  onClickEvent,
  resizingEventId,
  resizeHeight,
  onResizeStart,
  weather,
  showWeekends = true,
  hourHeightPx,
  weekViewAnchor = 'fixed',
  dimPastEvents = false,
  eventColorPriority = 'event',
  showConflictWarnings = false,
  minBreakBetweenEvents = 0,
}: WeekViewProps) {
  const allDays = useMemo(() => {
    if (weekViewAnchor === 'rolling') {
      const days: Date[] = [];
      for (let i = 0; i < 7; i++) {
        days.push(addDays(currentDate, i));
      }
      return days;
    }
    return getWeekDays(currentDate, weekStartDay);
  }, [currentDate, weekStartDay, weekViewAnchor]);

  const days = useMemo(
    () => (showWeekends ? allDays : allDays.filter((d) => d.getDay() !== 0 && d.getDay() !== 6)),
    [allDays, showWeekends],
  );

  // All-day events per day
  const allDayColumns = days.map((day) => ({
    date: day,
    events: getAllDayEvents(events, day),
  }));

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Day headers */}
      <div className="flex border-b">
        <div className="w-14 shrink-0" />
        <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)` }}>
          {days.map((day) => {
            const today = isToday(day);
            const dateKey = formatDateKey(day);
            const dayWeather = weather?.get(dateKey);
            return (
              <div
                key={dateKey}
                className={`text-center py-1.5 border-r last:border-r-0 ${
                  today ? 'bg-primary/5' : ''
                }`}
              >
                <div className="text-[10px] text-muted-foreground uppercase">
                  {DAY_NAMES[day.getDay()]}
                </div>
                <div
                  className={`text-sm font-medium ${
                    today
                      ? 'bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center mx-auto'
                      : ''
                  }`}
                >
                  {day.getDate()}
                </div>
                {dayWeather && (
                  <div className="flex items-center justify-center gap-0.5 mt-0.5">
                    <WeatherIcon code={dayWeather.weatherCode} size={14} />
                    <span className="text-[10px] text-muted-foreground">
                      {Math.round(dayWeather.tempMax)}°
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* All-day row */}
      <AllDayRow
        dayColumns={allDayColumns}
        onClickEvent={onClickEvent}
        eventColorPriority={eventColorPriority}
      />

      {/* Time grid */}
      <TimeGrid
        days={days}
        events={events}
        workingHoursStart={workingHoursStart}
        timeFormat={timeFormat}
        onClickSlot={onClickSlot}
        onClickEvent={onClickEvent}
        resizingEventId={resizingEventId}
        resizeHeight={resizeHeight}
        onResizeStart={onResizeStart}
        hourHeightPx={hourHeightPx}
        dimPastEvents={dimPastEvents}
        eventColorPriority={eventColorPriority}
        showConflictWarnings={showConflictWarnings}
        minBreakBetweenEvents={minBreakBetweenEvents}
      />
    </div>
  );
}
